import { InputAnimatedLabel } from "@web/components/ui/input-animated-label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@web/components/ui/dropdown-menu";
import { AlertTriangleIcon, ChevronDownIcon } from "lucide-react";
import { BrandSelectData } from "@shared/types";
import { cn } from "@web/lib/shadcn";

type DoctorBrandDropdownProps = {
  brandsList: BrandSelectData[];
  value?: BrandSelectData;
  readOnly?: boolean;                           // If readOnly is true, the component can be edit
  onSelect: (brand: BrandSelectData) => void;   // 
};

export function DoctorBrandDropdown({
  brandsList,
  value,
  readOnly = true,
  onSelect
}: DoctorBrandDropdownProps) {

  if (!brandsList.length) {
    return (
      <div className="relative w-full" role="alert">
        <InputAnimatedLabel
          label="Brand"
          value="No brands available"
          readOnly={true}
          aria-readonly={true}
          aria-disabled={true}
          className={cn(
            "border-red-500 border-2",
            "bg-red-50 text-red-700",
            "cursor-not-allowed opacity-70"
          )}
        />
        <AlertTriangleIcon 
          className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500" 
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger 
        asChild 
        disabled={readOnly}
        aria-disabled={readOnly}
      >
        <div 
          className={cn(
            "relative w-full",
            readOnly && "cursor-not-allowed"
          )}
        >
          <InputAnimatedLabel
            label="Brand"
            value={value?.name ?? ''}
            readOnly={readOnly}
            aria-readonly={readOnly}
            aria-disabled={readOnly}
            className={readOnly ? "" : "border-blue-500 border-2"}
          />
          {!readOnly && (
            <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true" />
          )}
        </div>
      </DropdownMenuTrigger>

      {!readOnly && (
        <DropdownMenuContent 
          className="bg-white shadow-lg rounded-lg border" 
          role="listbox"
          aria-label="Select a brand"
        >
          {brandsList.map((brand) => (
            <DropdownMenuItem
              key={brand.id}
              onSelect={() => { onSelect(brand) }}
              className={cn(
                "px-4 py-2 cursor-pointer",
                "hover:bg-gray-100 focus:bg-gray-100",
                "focus:outline-none focus:ring-2 focus:ring-blue-500"
              )}
            >
              {brand.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}

    </DropdownMenu>
  );
}